// src/components/admin/AdminConsole.jsx
import React, { useEffect, useRef, useState } from "react";
import { db } from "@/firebase";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  startAfter,
} from "firebase/firestore";

const PAGE_SIZE = 25;

/** Collections where you can only edit existing docs (no create/delete) */
const RESTRICTED_CREATE = new Set(["hero", "carouselImages", "contact", "contact_info", "contactInfo"]);

/** ---- TEMPLATES ---- */
const DOC_TEMPLATES = {
  departments: {
    name: { en: "", fr: "" },
    imageUrl: "",
    alt: "",
    description: { en: "", fr: "" },
    metaTitle: { en: "", fr: "" },
    metaDescription: { en: "", fr: "" },
    metaOgTitle: { en: "", fr: "" },
    metaOgDescription: { en: "", fr: "" },
    metaOgImage: "",
  },

  news: {
    title: { en: "", fr: "" },
    shortDescription: { en: "", fr: "" },
    post: { en: "", fr: "" },
    imageUrl: "",
    alt: "",
    images: [""],
    estimatedTimeToRead: 0,
    publishDate: new Date(),
    metaTitle: { en: "", fr: "" },
    metaDescription: { en: "", fr: "" },
    metaOgTitle: { en: "", fr: "" },
    metaOgDescription: { en: "", fr: "" },
    metaOgImage: "",
  },

  services: {
    title: { en: "", fr: "" },
    shortDescription: { en: "", fr: "" },
    post: { en: "", fr: "" },
    imageUrl: "",
    alt: "",
    images: [""],
    estimatedTimeToRead: 0,
    publishedDate: new Date(),
    metaTitle: { en: "", fr: "" },
    metaDescription: { en: "", fr: "" },
    metaOgTitle: { en: "", fr: "" },
    metaOgDescription: { en: "", fr: "" },
    metaOgImage: "",
  },

  // ✅ NEW: MetaTags collection
  MetaTags: {
    // optional: identify which route this doc is for
    pageName: "", // e.g. "/about-us"

    metaTitle:        { en: "", fr: "" },
    metaDescription:  { en: "", fr: "" },
    metaOgTitle:      { en: "", fr: "" },
    metaOgDescription:{ en: "", fr: "" },
    metaOgImage:      "",    // single URL
  },
    "about-us": {
    title: { en: "", fr: "" },
    paragraph1: { en: "", fr: "" },
    paragraph2: { en: "", fr: "" },
    paragraph3: { en: "", fr: "" },
  },

};


const SUB_TEMPLATES = {
  departments: {
    name: { en: "", fr: "" },
    imageUrl: "",
    alt:"",
    description: { en: "", fr: "" },
    metaTitle: { en: "", fr: "" },
    metaDescription: { en: "", fr: "" },
    metaOgTitle: { en: "", fr: "" },
    metaOgDescription: { en: "", fr: "" },
    metaOgImage: "",
  },
  
};


const ARTICLE_TEMPLATES = {
  departments: {
    imageUrl: [""],
    alt: "",
    product_description: { en: "", fr: "" },
    manufacture_description: { en: "", fr: "" },
    manufacture_name: "",
  },
  promotions: {
    imageUrl: [""],
    alt: "",
    description: { en: "", fr: "" },
    manufacture_name: "",
  },
};

const getDocTemplate = (c) => ({ ...(DOC_TEMPLATES[c] || {}) });
const getSubTemplate = (c) => ({ ...(SUB_TEMPLATES[c] || {}) });
const getArticleTemplate = (c) => ({ ...(ARTICLE_TEMPLATES[c] || {}) });

/** Discover collections via __collections or env fallback */
async function loadKnownCollections() {
  try {
    const snap = await getDocs(collection(db, "__collections"));
    if (!snap.empty) {
      return snap.docs
        .map((d) => (d.data()?.name || d.id || "").trim())
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b));
    }
  } catch {}
  const csv = (import.meta.env.VITE_ADMIN_COLLECTIONS || "").trim();
  return csv
    ? csv.split(",").map((s) => s.trim()).filter(Boolean).sort((a, b) => a.localeCompare(b))
    : [];
}

/** Helpers */
const toDatetimeLocalValue = (v) => {
  try {
    const d =
      v && typeof v?.toDate === "function" ? v.toDate() :
      v instanceof Date ? v :
      v ? new Date(v) : null;
    if (!d || isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
};

function slugify(s = "") {
  return String(s)
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 80);
}
/** Labeled divider to separate sections */
function SectionBreak({ label = "", className = "my-8" }) {
  return (
    <div className={`relative ${className}`}>
      <div className="border-t border-gray-200"></div>
      {label ? (
        <span className="absolute left-1/2 -translate-x-1/2 -top-3 bg-white px-3 text-[11px] uppercase tracking-wide text-gray-500">
          {label}
        </span>
      ) : null}
    </div>
  );
}


/** Simple boxed panel for editor bodies */
function Panel({ children }) {
  return <div className="rounded-2xl border bg-white p-4 shadow-sm">{children}</div>;
}

/** Collapsible with header strip + boxed content */
function Collapsible({ title, open, onToggle, children }) {
  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 rounded-xl border bg-gray-50 hover:bg-gray-100"
      >
        <span className="text-sm font-medium text-gray-800">{title}</span>
        <span className={`text-gray-600 transition-transform ${open ? "rotate-180" : ""}`}>▾</span>
      </button>
      {open && <Panel>{children}</Panel>}
    </div>
  );
}

async function findAvailableId(basePath, wantedId) {
  const tryId = async (id) => {
    const r = await getDoc(doc(db, basePath, id));
    return r.exists() ? null : id;
  };
  const first = await tryId(wantedId);
  if (first) return first;
  for (let i = 2; i <= 20; i++) {
    const candidate = await tryId(`${wantedId}-${i}`);
    if (candidate) return candidate;
  }
  return null;
}

function FieldRow({ k, v, onChange, onDelete }) {
  const [showJson, setShowJson] = useState(false);
  const isComplex = Array.isArray(v) || (v && typeof v === "object");
  const t = isComplex ? "complex" : typeof v;

  if (t === "boolean") {
    return (
      <div className="flex items-center gap-3">
        <label className="w-44 text-sm text-gray-600">{k}</label>
        <input type="checkbox" checked={!!v} onChange={(e) => onChange(k, e.target.checked)} />
        <button className="text-xs text-red-600 ml-auto" onClick={() => onDelete(k)}>remove</button>
      </div>
    );
  }

  if (t === "number") {
    return (
      <div className="flex items-center gap-3">
        <label className="w-44 text-sm text-gray-600">{k}</label>
        <input
          type="number"
          className="flex-1 border rounded-lg px-3 py-1"
          value={v ?? ""}
          onChange={(e) => onChange(k, e.target.value === "" ? null : Number(e.target.value))}
        />
        <button className="text-xs text-red-600" onClick={() => onDelete(k)}>remove</button>
      </div>
    );
  }

  if (!isComplex) {
    return (
      <div className="flex items-center gap-3">
        <label className="w-44 text-sm text-gray-600">{k}</label>
        <input
          type="text"
          className="flex-1 border rounded-lg px-3 py-1"
          value={v ?? ""}
          onChange={(e) => onChange(k, e.target.value)}
        />
        <button className="text-xs text-red-600" onClick={() => onDelete(k)}>remove</button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <label className="w-44 text-sm text-gray-600">{k}</label>
        <span className="text-xs text-gray-500">array/object</span>
        <button className="ml-auto text-xs underline" onClick={() => setShowJson((s) => !s)}>
          {showJson ? "Hide" : "Edit as JSON"}
        </button>
        <button className="text-xs text-red-600" onClick={() => onDelete(k)}>remove</button>
      </div>
      {showJson && (
        <textarea
          className="w-full border rounded-lg p-2 font-mono text-xs"
          rows={5}
          value={JSON.stringify(v ?? {}, null, 2)}
          onChange={(e) => {
            try { onChange(k, JSON.parse(e.target.value || "null")); } catch {}
          }}
        />
      )}
    </div>
  );
}

function ImageListField({ k, v, onChange, onDelete }) {
  const list = Array.isArray(v) ? v : [];
  const update = (idx, val) => {
    const next = [...list];
    next[idx] = val;
    onChange(k, next);
  };
  const addRow = () => onChange(k, [...list, ""]);
  const removeRow = (idx) => onChange(k, list.filter((_, i) => i !== idx));

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <label className="w-44 text-sm text-gray-600">{k}</label>
        <span className="text-xs text-gray-500">list of image URLs</span>
        <button className="ml-auto text-xs underline" onClick={addRow}>Add image</button>
        <button className="text-xs text-red-600" onClick={() => onDelete(k)}>remove</button>
      </div>
      <div className="space-y-2">
        {list.length === 0 && <div className="text-xs text-gray-500">No images yet.</div>}
        {list.map((url, idx) => (
          <div key={idx} className="flex items-center gap-2">
            <input
              type="text"
              className="flex-1 border rounded-lg px-3 py-1 text-xs"
              placeholder="https://…"
              value={url || ""}
              onChange={(e) => update(idx, e.target.value)}
            />
            <button className="text-xs text-red-600" onClick={() => removeRow(idx)}>remove</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function TimestampField({ k, v, onChange, onDelete }) {
  const value = toDatetimeLocalValue(v);
  return (
    <div className="flex items-center gap-3">
      <label className="w-44 text-sm text-gray-600">{k}</label>
      <input
        type="datetime-local"
        className="flex-1 border rounded-lg px-3 py-1"
        value={value}
        onChange={(e) => {
          const str = e.target.value;
          const d = str ? new Date(str) : null;
          onChange(k, d || null);
        }}
      />
      <button className="text-xs text-red-600 ml-auto" onClick={() => onDelete(k)}>clear</button>
    </div>
  );
}

function I18nField({ k, v, onChange, onDelete }) {
  const obj = (v && typeof v === "object") ? v : { en: "", fr: "" };
  const setLang = (lang, val) => onChange(k, { ...obj, [lang]: val });

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <label className="w-44 text-sm text-gray-600">{k}</label>
        <span className="text-xs text-gray-500">EN / FR</span>
        <button className="text-xs text-red-600 ml-auto" onClick={() => onDelete(k)}>remove</button>
      </div>
      <div className="space-y-2">
        <div>
          <label className="block text-xs text-gray-500 mb-1">English</label>
          <textarea
            className="w-full border rounded-lg p-2 text-sm"
            rows={1}
            value={obj.en || ""}
            onChange={(e) => setLang("en", e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Français</label>
          <textarea
            className="w-full border rounded-lg p-2 text-sm"
            rows={1}
            value={obj.fr || ""}
            onChange={(e) => setLang("fr", e.target.value)}
          />
        </div>
      </div>
    </div>
  );
}

export default function AdminConsole() {
  // Collections
  const [collections, setCollections] = useState([]);
  const [collectionName, setCollectionName] = useState("");

  // Section open state
  const [docOpen, setDocOpen] = useState(true);
  const [subOpen, setSubOpen] = useState(true);
  const [articleOpen, setArticleOpen] = useState(true);

  // Docs
  const [docOptions, setDocOptions] = useState([]); // [{id}]
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const lastVisibleRef = useRef(null);
  const [selectedDocId, setSelectedDocId] = useState("");
  const [docData, setDocData] = useState({});

  // Subdepartments
  const [subDocOptions, setSubDocOptions] = useState([]); // [{id}]
  const [selectedSubDocId, setSelectedSubDocId] = useState("");
  const [subDocData, setSubDocData] = useState({});
  const [newSubId, setNewSubId] = useState("");

  // Articles
  const [articleOptions, setArticleOptions] = useState([]); // [{id}]
  const [selectedArticleId, setSelectedArticleId] = useState("");
  const [articleData, setArticleData] = useState({});
  const [newArticleId, setNewArticleId] = useState("");

  // Misc
  const [status, setStatus] = useState("");
  const [orderByField, setOrderByField] = useState("__name__");

  const createLocked = RESTRICTED_CREATE.has(collectionName);
  const isDept = collectionName === "departments";
  const isPromo = collectionName === "promotions";

  const articlesBasePath = (docId, subId) => {
    if (isDept) return `${collectionName}/${docId}/subdepartments/${subId}/articles`;
    if (isPromo) return `${collectionName}/${docId}/articles`;
    return "";
  };

  // hydrate top-level new doc
  useEffect(() => {
    if (!collectionName) return;
    if (!selectedDocId && Object.keys(docData || {}).length === 0) {
      setDocData(getDocTemplate(collectionName));
    }
  }, [collectionName, selectedDocId]); // eslint-disable-line

  // bootstrap collections
  useEffect(() => {
    (async () => {
      const list = await loadKnownCollections();
      setCollections(list);
      if (list.length) setCollectionName((c) => c || list[0]);
    })();
  }, []);


// --- auto-collapse rules 
useEffect(() => {
  const creatingSub =
    isDept && selectedDocId && !selectedSubDocId && Object.keys(subDocData).length > 0;

  // Collapse the department editor whenever you’re in a sub context (selected or creating)
  setDocOpen(!(isDept && selectedDocId && (selectedSubDocId || creatingSub)));

  // ✅ Collapse the sub editor as soon as a sub is selected.
  // Open it only when creating a new sub (no selectedSubDocId).
  if (isDept && selectedDocId && selectedSubDocId) {
    // setSubOpen(false);
  } else {
    setSubOpen(creatingSub); // open while creating a new sub
  }

  // Article panel: open whenever its section is visible
  setArticleOpen(
    !!((isDept && selectedDocId && selectedSubDocId) || (isPromo && selectedDocId))
  );
}, [isDept, isPromo, selectedDocId, selectedSubDocId, subDocData]);

  // derive subdocument ID from name
  useEffect(() => {
    if (!isDept || !selectedDocId) return;
    const en = subDocData?.name?.en?.trim() || "";
    const fr = subDocData?.name?.fr?.trim() || "";
    const slug = slugify(en || fr);
    setNewSubId(slug);
  }, [isDept, selectedDocId, subDocData?.name?.en, subDocData?.name?.fr]);

  // when collection changes -> reload docs and clear nested
  useEffect(() => {
    if (!collectionName) return;
    resetDocLevel();
    resetSubLevel();
    resetArticleLevel();
    void loadFirstPage();
  }, [collectionName, orderByField]); // eslint-disable-line

  // seed article template when panel opens
  useEffect(() => {
    const panelOpen =
      (isDept && selectedDocId && selectedSubDocId) ||
      (isPromo && selectedDocId);

    if (panelOpen && !selectedArticleId) {
      setArticleData(getArticleTemplate(collectionName));
    }
  }, [collectionName, isDept, isPromo, selectedDocId, selectedSubDocId, selectedArticleId]);

  // auto-pick first doc for restricted collections
  useEffect(() => {
    if (RESTRICTED_CREATE.has(collectionName) && !selectedDocId && docOptions.length > 0) {
      onPickDoc(docOptions[0].id);
    }
  }, [collectionName, docOptions, selectedDocId]);

  // Ensure sub template shows when creating a new subdepartment
  useEffect(() => {
    if (isDept && selectedDocId && !selectedSubDocId && Object.keys(subDocData).length === 0) {
      setSubDocData(getSubTemplate(collectionName));
    }
  }, [isDept, collectionName, selectedDocId, selectedSubDocId, subDocData]);

  // ---------- reset helpers ----------
  function resetDocLevel() {
    setSelectedDocId("");
    setDocData({});
    setDocOptions([]);
    setHasMore(false);
    lastVisibleRef.current = null;
  }
  function resetSubLevel() {
    setSelectedSubDocId("");
    setSubDocData({});
    setSubDocOptions([]);
    setNewSubId("");
  }
  function resetArticleLevel() {
    setSelectedArticleId("");
    setArticleData({});
    setArticleOptions([]);
    setNewArticleId("");
  }

  // ---------- documents ----------
  async function loadFirstPage({ clearStatus = true } = {}) {
    setLoadingDocs(true);
    setStatus("");
    try {
      const base = collection(db, collectionName);
      const qy =
        orderByField && orderByField !== "__name__"
          ? query(base, orderBy(orderByField), limit(PAGE_SIZE))
          : query(base, orderBy("__name__"), limit(PAGE_SIZE));

      const snap = await getDocs(qy);
      const rows = snap.docs.map((d) => ({ id: d.id }));
      setDocOptions(rows);

      if ((RESTRICTED_CREATE.has(collectionName) || rows.length === 1) && rows[0]) {
        await onPickDoc(rows[0].id);
      }

      const last = snap.docs[snap.docs.length - 1] || null;
      lastVisibleRef.current = last;
      setHasMore(!!last);
    } catch (e) {
      setStatus("Load error: " + e.message);
    } finally {
      setLoadingDocs(false);
    }
  }

  async function loadMore() {
    if (!hasMore || !lastVisibleRef.current) return;
    setLoadingDocs(true);
    try {
      const base = collection(db, collectionName);
      const qy =
        orderByField && orderByField !== "__name__"
          ? query(base, orderBy(orderByField), startAfter(lastVisibleRef.current), limit(PAGE_SIZE))
          : query(base, orderBy("__name__"), startAfter(lastVisibleRef.current), limit(PAGE_SIZE));
      const snap = await getDocs(qy);
      const rows = snap.docs.map((d) => ({ id: d.id }));
      setDocOptions((prev) => [...prev, ...rows]);
      const last = snap.docs[snap.docs.length - 1] || null;
      lastVisibleRef.current = last;
      setHasMore(!!last);
    } catch (e) {
      setStatus("Load error: " + e.message);
    } finally {
      setLoadingDocs(false);
    }
  }

async function onPickDoc(id) {
  setSelectedDocId(id);
  setStatus("");
  resetSubLevel();
  resetArticleLevel();

  // If no ID selected → hydrate using default template
  if (!id) {
    setDocData(getDocTemplate(collectionName));
    return;
  }

  try {
    const ref = doc(db, collectionName, id);
    const snap = await getDoc(ref);

    const template = getDocTemplate(collectionName); // may be {}
    const data = snap.exists() ? (snap.data() || {}) : {};

    // Merge template + data → guarantees all template fields exist in the form
    const merged = { ...template, ...data };

    // Special case: keep MetaTags.pageName in sync with doc ID
    if (collectionName === "MetaTags") {
      merged.pageName = id || merged.pageName || "";
    }

    setDocData(merged);
  } catch (e) {
    setStatus("Read error: " + e.message);
    return;
  }

  // Department / promotions logic unchanged
  if (isDept) {
    await loadSubList(id);
  } else if (isPromo) {
    await loadArticleList(id, null);
  }
}



async function saveDoc() {
  setStatus("");

const normalizeDept = (src) => {
  const name =
    src?.name && typeof src.name === "object" ? src.name : {};

  return {
    ...src,  // << keep all meta fields + future fields
    imageUrl: typeof src?.imageUrl === "string" ? src.imageUrl : "",
    name: { en: name.en ?? "", fr: name.fr ?? "" },
  };
};


  try {
    // ✅ UPDATE existing MetaTags doc
    if (collectionName === "MetaTags" && selectedDocId) {
      const payload = {
        ...docData,
        pageName: selectedDocId, // keep in sync with ID
        updatedAt: serverTimestamp(),
      };
      await setDoc(doc(db, "MetaTags", selectedDocId), payload, { merge: true });
      setStatus(`Saved MetaTags/${selectedDocId}.`);
      return;
    }

    // 🔁 Generic update for all other collections
    if (selectedDocId) {
      const payload = { ...docData, updatedAt: serverTimestamp() };
      await setDoc(doc(db, collectionName, selectedDocId), payload, { merge: true });
      setStatus(`Saved ${collectionName}/${selectedDocId}.`);
      return;
    }

    // 🌿 Special create logic: departments
    if (collectionName === "departments") {
      const wanted =
        (docData?.name?.en && docData.name.en.trim()) ||
        (docData?.name?.fr && docData.name.fr.trim()) ||
        "";

      if (!wanted) {
        setStatus("Please enter the department name (EN or FR).");
        return;
      }
      if (wanted.includes("/")) {
        setStatus("Department name cannot contain '/'. Please remove it.");
        return;
      }

      const base = normalizeDept(docData);
      const payload = { ...base, updatedAt: serverTimestamp() };

      const ref = doc(db, "departments", wanted);
      const exists = await getDoc(ref);
      if (exists.exists()) {
        setStatus(`Department '${wanted}' already exists. Choose a different name.`);
        return;
      }

      await setDoc(ref, payload, { merge: true });
      setStatus(`Created departments/${wanted}.`);
      await loadFirstPage();
      setSelectedDocId(wanted);
      return;
    }

    // 🌟 Special create logic: MetaTags → doc ID = pageName
    if (collectionName === "MetaTags") {
      const pageName = (docData?.pageName || "").trim();

      if (!pageName) {
        setStatus("Please enter a pageName for this meta entry.");
        return;
      }

      const ref = doc(db, "MetaTags", pageName);
      const exists = await getDoc(ref);
      if (exists.exists()) {
        setStatus(
          `MetaTags/${pageName} already exists. Pick a different pageName or select it from the dropdown.`
        );
        return;
      }

      const payload = {
        ...docData,
        pageName,
        updatedAt: serverTimestamp(),
      };

      await setDoc(ref, payload, { merge: true });
      setStatus(`Created MetaTags/${pageName}.`);
      await loadFirstPage();
      setSelectedDocId(pageName);
      return;
    }

    // 🧱 Default create for all other collections
    const payload = { ...docData, updatedAt: serverTimestamp() };
    const ref = await addDoc(collection(db, collectionName), payload);
    setStatus(`Created ${collectionName}/${ref.id}.`);
    await loadFirstPage();
    setSelectedDocId(ref.id);
  } catch (e) {
    setStatus("Save error: " + e.message);
  }
}


  async function deleteDocNow() {
    if (!selectedDocId) { setStatus("Pick a document to delete."); return; }
    if (createLocked) { setStatus(`Delete disabled for '${collectionName}'.`); return; }
    setStatus("");
    try {
      await deleteDoc(doc(db, collectionName, selectedDocId));
      setStatus(`Deleted ${collectionName}/${selectedDocId}.`);
      resetDocLevel();
      resetSubLevel();
      resetArticleLevel();
      await loadFirstPage();
    } catch (e) {
      setStatus("Delete error: " + e.message);
    }
  }

  // ---------- subdepartments (for departments) ----------
  async function loadSubList(forDocId) {
    const deptId = forDocId || selectedDocId;
    setSubDocOptions([]);
    setSelectedSubDocId("");
    setSubDocData({});
    setNewSubId("");
    if (!deptId) return;

    try {
      const base = collection(db, `${collectionName}/${deptId}/subdepartments`);
      const snap = await getDocs(query(base, orderBy("__name__"), limit(PAGE_SIZE)));
      setSubDocOptions(snap.docs.map((d) => ({ id: d.id })));
    } catch (e) {
      setStatus("Load subdepartments error: " + e.message);
    }
  }

  async function onPickSubDoc(id) {
    setSelectedSubDocId(id);
    resetArticleLevel();

    if (!id) {
      setSubDocData(getSubTemplate(collectionName));
      setNewSubId("");
      return;
    }

   try {
    const ref = doc(db, `${collectionName}/${selectedDocId}/subdepartments`, id);
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      setSubDocData({});
      setStatus("Subdepartment not found.");
      return;
    }

    const template = getSubTemplate(collectionName);
    const data = snap.data() || {};
    const merged = { ...template, ...data };

    setSubDocData(merged);
    setNewSubId("");
  } catch (e) {
    setStatus("Read subdepartment error: " + e.message);
    return;
  }


    await loadArticleList(selectedDocId, id);
  }

  async function saveSubDoc() {
    if (!selectedDocId) { setStatus("Pick a department first."); return; }
    setStatus("");
    const path = `${collectionName}/${selectedDocId}/subdepartments`;

    try {
      const payload = { ...subDocData, updatedAt: serverTimestamp() };

      if (selectedSubDocId) {
        await setDoc(doc(db, path, selectedSubDocId), payload, { merge: true });
        setStatus(`Saved subdepartments/${selectedSubDocId}.`);
        return;
      }

      let wantedId = (newSubId || "").trim();

      if (!wantedId) {
        const en = subDocData?.name?.en || "";
        const fr = subDocData?.name?.fr || "";
        const derived = slugify(en || fr);
        if (derived) wantedId = derived;
      }

      if (wantedId) {
        const finalId = await findAvailableId(path, wantedId);
        if (finalId) {
          await setDoc(doc(db, path, finalId), payload, { merge: true });
          setStatus(`Created subdepartments/${finalId}.`);
          await loadSubList(selectedDocId);
          setSelectedSubDocId(finalId);
          setNewSubId("");
          return;
        }
      }

      const ref = await addDoc(collection(db, path), payload);
      setStatus(`Created subdepartments/${ref.id}.`);
      await loadSubList(selectedDocId);
      setSelectedSubDocId(ref.id);
      setNewSubId("");
    } catch (e) {
      setStatus("Save subdepartment error: " + e.message);
    }
  }

  async function deleteSubDocNow() {
    if (!selectedDocId || !selectedSubDocId) { setStatus("Pick a subdepartment to delete."); return; }
    const path = `${collectionName}/${selectedDocId}/subdepartments`;
    try {
      await deleteDoc(doc(db, path, selectedSubDocId));
      setStatus(`Deleted subdepartments/${selectedSubDocId}.`);
      setSelectedSubDocId("");
      setSubDocData({});
      await loadSubList(selectedDocId);
      resetArticleLevel();
    } catch (e) {
      setStatus("Delete subdepartment error: " + e.message);
    }
  }

  // ---------- articles ----------
  async function loadArticleList(forDocId, forSubId) {
    const docId = forDocId || selectedDocId;
    const subId = forSubId || selectedSubDocId;

    setArticleOptions([]);
    setSelectedArticleId("");
    setArticleData({});

    if (isDept && (!docId || !subId)) return;
    if (isPromo && !docId) return;

    try {
      const base = collection(db, articlesBasePath(docId, subId));
      const snap = await getDocs(query(base, orderBy("__name__"), limit(PAGE_SIZE)));
      setArticleOptions(snap.docs.map((d) => ({ id: d.id })));
    } catch (e) {
      setStatus("Load articles error: " + e.message);
    }
  }

async function onPickArticle(id) {
  setSelectedArticleId(id);
  if (!id) {
    setArticleData(getArticleTemplate(collectionName));
    setNewArticleId("");
    return;
  }

  try {
    const ref = doc(db, articlesBasePath(selectedDocId, selectedSubDocId), id);
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      setArticleData({});
      setStatus("Article not found.");
      return;
    }

    const template = getArticleTemplate(collectionName);
    const data = snap.data() || {};
    const merged = { ...template, ...data };

    setArticleData(merged);
    setNewArticleId("");
  } catch (e) {
    setStatus("Read article error: " + e.message);
  }
}


  async function saveArticle() {
    if ((isDept && (!selectedDocId || !selectedSubDocId)) || (isPromo && !selectedDocId)) {
      setStatus(isDept ? "Pick a subdepartment first." : "Pick a promotion document first.");
      return;
    }

    setStatus("");
    const basePath = articlesBasePath(selectedDocId, selectedSubDocId);

    const template = getArticleTemplate(collectionName);
    const normalizeI18n = (v) => {
      const obj = (v && typeof v === "object") ? v : {};
      return { en: obj.en ?? "", fr: obj.fr ?? "" };
    };
    const normalizePayload = (src) => {
      const raw = { ...src };
      delete raw.title;

      const merged = { ...template, ...raw };
      merged.imageUrl = Array.isArray(merged.imageUrl) ? merged.imageUrl.filter(Boolean) : [];
      if ("product_description" in merged) merged.product_description = normalizeI18n(merged.product_description);
      if ("manufacture_description" in merged) merged.manufacture_description = normalizeI18n(merged.manufacture_description);
      if ("description" in merged) merged.description = normalizeI18n(merged.description);
      merged.manufacture_name = typeof merged.manufacture_name === "string" ? merged.manufacture_name : "";

      return merged;
    };

    try {
      if (selectedArticleId) {
        const payload = { ...normalizePayload(articleData), updatedAt: serverTimestamp() };
        await setDoc(doc(db, basePath, selectedArticleId), payload, { merge: true });
        setArticleData(payload);
        setStatus(`Saved articles/${selectedArticleId}.`);
        return;
      }

      const wanted = (newArticleId || "").trim();
      if (!wanted) { setStatus("Please enter an Article ID."); return; }
      if (!/^[A-Za-z0-9_-]+$/.test(wanted)) {
        setStatus("Article ID can only contain letters, numbers, hyphens, and underscores.");
        return;
      }

      const targetRef = doc(db, basePath, wanted);
      const exists = await getDoc(targetRef);
      if (exists.exists()) {
        setStatus(`Article ID '${wanted}' already exists. Choose a different ID.`);
        return;
      }

      const payload = { ...normalizePayload(articleData), updatedAt: serverTimestamp() };
      await setDoc(targetRef, payload, { merge: true });

      await loadArticleList(selectedDocId, selectedSubDocId);
      setSelectedArticleId(wanted);
      setArticleData(payload);
      setNewArticleId("");

      setStatus(`Created articles/${wanted}.`);
    } catch (e) {
      setStatus("Save article error: " + e.message);
    }
  }

  async function deleteArticleNow() {
    if (!selectedArticleId) { setStatus("Pick an article to delete."); return; }
    const basePath = articlesBasePath(selectedDocId, selectedSubDocId);
    try {
      await deleteDoc(doc(db, basePath, selectedArticleId));
      setStatus(`Deleted articles/${selectedArticleId}.`);
      setSelectedArticleId("");
      setArticleData({});
      await loadArticleList(selectedDocId, selectedSubDocId);
    } catch (e) {
      setStatus("Delete article error: " + e.message);
    }
  }

  // ---------- generic field handlers ----------
  const updateField = (setter) => (key, value) => setter((d) => ({ ...d, [key]: value }));
  const removeField = (setter) => (key) => setter((d) => { const c = { ...d }; delete c[key]; return c; });

  const renderField = (setter) => ([k, v]) => {
  if (
    [
      "name",
      "title",
      "shortDescription",
      "post",
      "body",
      "description",
      "product_description",
      "manufacture_description",
      // 🔹 SEO / meta fields use EN/FR too
      "metaTitle",
      "metaDescription",
      "metaOgTitle",
      "metaOgDescription",
      "paragraph1",
      "paragraph2",
      "paragraph3",
    ].includes(k)
  ) {
    return (
      <I18nField
        key={k}
        k={k}
        v={v}
        onChange={updateField(setter)}
        onDelete={removeField(setter)}
      />
    );
  }

  if (k === "images") {
    const list = Array.isArray(v) ? v : [""];
    return (
      <ImageListField
        key={k}
        k={k}
        v={list}
        onChange={updateField(setter)}
        onDelete={removeField(setter)}
      />
    );
  }

  if (Array.isArray(v) && k === "imageUrl") {
    return (
      <ImageListField
        key={k}
        k={k}
        v={v}
        onChange={updateField(setter)}
        onDelete={removeField(setter)}
      />
    );
  }

  if (k === "publishDate" || k === "publishedDate") {
    return (
      <TimestampField
        key={k}
        k={k}
        v={v}
        onChange={updateField(setter)}
        onDelete={removeField(setter)}
      />
    );
  }

  return (
    <FieldRow
      key={k}
      k={k}
      v={v}
      onChange={updateField(setter)}
      onDelete={removeField(setter)}
    />
  );
};

  return (
    <div className="mx-auto p-4 md:p-6 max-w-3xl">
      <h1 className="text-2xl font-semibold mb-4">Admin Console</h1>

      {/* Collection selector */}
      <div className="mb-3">
        <label className="block text-sm text-gray-600 mb-1">Collection</label>
        <select
          className="w-full border rounded-xl px-3 py-2"
          value={collectionName}
          onChange={(e) => setCollectionName(e.target.value)}
        >
          {collections.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <div className="flex items-center gap-2 mt-2">
          <label className="text-xs text-gray-500">order by</label>
          <input
            className="border rounded-lg px-2 py-1 text-xs w-40"
            placeholder="__name__ or a field"
            value={orderByField}
            onChange={(e) => setOrderByField(e.target.value.trim() || "__name__")}
          />
          <button className="ml-auto text-xs underline" onClick={loadFirstPage} disabled={!collectionName}>
            reload
          </button>
        </div>
      </div>

      {/* Documents */}
      <div className="mb-4">
        <label className="block text-sm text-gray-600 mb-1">Document</label>
        <select
          className="w-full border rounded-xl px-3 py-2"
          value={selectedDocId}
          onChange={(e) => onPickDoc(e.target.value)}
        >
          {!createLocked && <option value="">— New document —</option>}
          {docOptions.map((d) => (
            <option key={d.id} value={d.id}>{d.id}</option>
          ))}
        </select>

        <div className="mt-2 flex items-center gap-2">
          <button
            className="rounded-xl px-3 py-2 text-sm bg-gray-900 text-white disabled:opacity-50"
            onClick={loadMore}
            disabled={!hasMore || loadingDocs}
          >
            {hasMore ? "Load more" : "No more"}
          </button>
          {loadingDocs && <span className="text-xs text-gray-500">Loading…</span>}
        </div>
      </div>

      {/* Subdepartments (only for departments) */}
      {isDept && selectedDocId && (
        <div className="mb-4">
          <label className="block text-sm text-gray-600 mb-1">
            Subdepartment (in {selectedDocId})
          </label>
          <select
            className="w-full border rounded-xl px-3 py-2"
            value={selectedSubDocId}
            onChange={(e) => onPickSubDoc(e.target.value)}
          >
            <option value="">— New subdepartment —</option>
            {subDocOptions.map((d) => <option key={d.id} value={d.id}>{d.id}</option>)}
          </select>

    
        </div>
      )}

      {/* Articles selector */}
      {((isDept && selectedDocId && selectedSubDocId) || (isPromo && selectedDocId)) && (
        <div className="mb-4">
          <label className="block text-sm text-gray-600 mb-1">
            Article {isDept ? `(in ${selectedSubDocId})` : `(in ${selectedDocId})`}
          </label>
          <select
            className="w-full border rounded-xl px-3 py-2"
            value={selectedArticleId}
            onChange={(e) => onPickArticle(e.target.value)}
          >
            <option value="">— New article —</option>
            {articleOptions.map((d) => <option key={d.id} value={d.id}>{d.id}</option>)}
          </select>
        </div>
      )}
      <SectionBreak label="Editors" className="my-12 md:my-16" />


      {/* Editors as boxed panels */}
      <div className="space-y-4">
        {/* Top-level doc editor */}
        <Collapsible
          title={
            selectedDocId
              ? `Edit doc: ${selectedDocId}`
              : (createLocked ? "Select a document to edit" : "Create a new document")
          }
          open={docOpen}
          onToggle={() => setDocOpen((v) => !v)}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs text-gray-500">
              auto adds <code>updatedAt</code>
            </div>
          </div>

          <div className="space-y-2 max-h-[38vh] overflow-auto pr-1">
            {Object.keys(docData || {}).length === 0 && <div className="text-xs text-gray-500">No fields yet.</div>}
            {Object.entries(docData || {}).map(renderField(setDocData))}
          </div>

          <div className="pt-3 flex gap-2">
            <button
              className="rounded-xl px-4 py-2 bg-black text-white disabled:opacity-50"
              onClick={saveDoc}
              disabled={
                (createLocked && !selectedDocId) ||
                (
                  collectionName === "departments" &&
                  !selectedDocId &&
                  !(docData?.name?.en?.trim() || docData?.name?.fr?.trim())
                )
              }
            >
              {selectedDocId ? "Save changes" : (createLocked ? "Select a document" : "Create document")}
            </button>

            <button
              className="rounded-xl px-4 py-2 bg-red-600 text-white disabled:opacity-50"
              onClick={deleteDocNow}
              disabled={!selectedDocId || createLocked}
              title={createLocked ? "Deletion disabled for this collection." : ""}
            >
              Delete
            </button>
          </div>
          {/* ✅ Status message directly under the box */}
  {status && (
    <div className="mt-2 text-xs text-gray-600">
      {status}
    </div>
  )}
        </Collapsible>

        {/* Subdepartment editor */}
        {isDept && selectedDocId && (
          <Collapsible
            title={
              selectedSubDocId
                ? `Edit subdepartment: ${selectedSubDocId}`
                : `Create a new subdepartment in ${selectedDocId}`
            }
            open={subOpen}
            onToggle={() => setSubOpen((v) => !v)}
          >
            <div className="space-y-2 max-h-[30vh] overflow-auto pr-1">
              {Object.keys(subDocData || {}).length === 0 && <div className="text-xs text-gray-500">No fields yet.</div>}
              {Object.entries(subDocData || {}).map(renderField(setSubDocData))}
            </div>

            <div className="pt-3 flex gap-2">
              <button className="rounded-xl px-4 py-2 bg-black text-white" onClick={saveSubDoc}>
                {selectedSubDocId ? "Save subdepartment" : "Create subdepartment"}
              </button>
              <button
                className="rounded-xl px-4 py-2 bg-red-600 text-white disabled:opacity-50"
                onClick={deleteSubDocNow}
                disabled={!selectedSubDocId}
              >
                Delete
              </button>
            </div>
             {status && (
    <div className="mt-2 text-xs text-gray-600">
      {status}
    </div>
  )}
          </Collapsible>
        )}

        {/* Article editor */}
        {((isDept && selectedDocId && selectedSubDocId) || (isPromo && selectedDocId)) && (
          <Collapsible
            title={selectedArticleId ? `Edit article: ${selectedArticleId}` : "Create a new article"}
            open={articleOpen}
            onToggle={() => setArticleOpen((v) => !v)}
          >
            {!selectedArticleId && (
              <div className="mb-3">
                <label className="block text-xs text-gray-500 mb-1">Article ID (required)</label>
                <input
                  type="text"
                  placeholder="e.g. WP-FEN-001"
                  className="w-full border rounded-lg px-3 py-1 text-sm"
                  value={newArticleId}
                  onChange={(e) => setNewArticleId(e.target.value)}
                />
                <div className="text-[11px] text-gray-500 mt-1">
                  Use letters, numbers, hyphens, or underscores only (no spaces).
                </div>
              </div>
            )}

            <div className="space-y-2 max-h-[30vh] overflow-auto pr-1">
              {Object.keys(articleData || {}).length === 0 && <div className="text-xs text-gray-500">No fields yet.</div>}
              {Object.entries(articleData || {}).map(renderField(setArticleData))}
            </div>

            <div className="pt-3 flex gap-2">
              <button
                className="rounded-xl px-4 py-2 bg-black text-white disabled:opacity-50"
                onClick={saveArticle}
                disabled={!selectedArticleId && !newArticleId.trim()}
              >
                {selectedArticleId ? "Save article" : "Create article"}
              </button>
              <button
                className="rounded-xl px-4 py-2 bg-red-600 text-white disabled:opacity-50"
                onClick={deleteArticleNow}
                disabled={!selectedArticleId}
              >
                Delete
              </button>
            </div>
          </Collapsible>
        )}
      </div>

      <div className="text-xs text-gray-600 mt-3">{status || "—"}</div>
    </div>
  );
}
